<script>
  import { getContext } from "svelte";
  import { Modal as DangerModal } from "carbon-components-svelte";
  import { ROUTES } from "@utils/routes.js";
  import { Button, Tooltip } from "paxray-components-svelte";
  import { projectListStore } from "@stores/damProjectsStore.js";
  import { ARROW_LEFT_ICON } from "@utils/icons.js";
  import Checkmark16 from "carbon-icons-svelte/lib/Checkmark16";

  export let idproject;
  export let step;
  export let stage;
  export let toastMsg;
  export let saveSuccessful;
  export let showGoBackButton = true;
  export let showMarkAsCompletedButton = true;
  export let disabled = false;
  // Shown on hover while the button is disabled, line breaks are kept.
  export let disabledTooltip = "";
  export let allowAdminOverride = true;
  export let confirmBeforeComplete = true;
  export let showCompletedState = true;
  export let iconLeft = false;
  export let icon = null;
  export let text = "Mark as Completed";
  export let size = "md";
  export let completionWarning =
    "After completion, this step cannot be changed. This action cannot be undone.";
  export let onClick = handleMarkAsCompleted;
  // Optional summary lines below the warning in the confirmation dialog
  export let completionDetails = [];
  let completed = false;
  let confirmationOpen = false;
  const role = getContext("role");

  $: isAdmin = $role?.role === "admin";
  $: effectiveDisabled = disabled && !(allowAdminOverride && isAdmin);
  // Close an open confirmation once the step becomes blocked again.
  $: if (effectiveDisabled) confirmationOpen = false;

  const { markStepAsCompleted, isStepCompleted } = projectListStore;

  $: checkCompleted(idproject, step, stage);

  async function checkCompleted(idproject, step, stage) {
    if (!showCompletedState) return;
    completed = await isStepCompleted(idproject, step, stage);
  }

  async function handleMarkAsCompleted() {
    const data = await markStepAsCompleted(idproject, step, stage);
    if (data?.ok) {
      toastMsg = `${step} step marked as completed.`;
      saveSuccessful = true;
      completed = true;
      window.location.assign(`${ROUTES.dam.progress}?idproject=${idproject}`);
    } else {
      toastMsg = data?.message || data?.error || "An error occurred.";
      saveSuccessful = false;
    }
  }
  function handleGoBackToProgress() {
    window.location.assign(`${ROUTES.dam.progress}?idproject=${idproject}`);
  }

  function handleCompletionClick() {
    if (confirmBeforeComplete) {
      confirmationOpen = true;
      return;
    }

    onClick();
  }

  function confirmCompletion() {
    confirmationOpen = false;
    if (effectiveDisabled) return;
    onClick();
  }

  export function refresh() {
    checkCompleted(idproject, step, stage);
  }
</script>

{#if showGoBackButton}
  <Button
    size="field"
    kind="secondary"
    text={`Back to Progress`}
    on:click={handleGoBackToProgress}
    iconLeft
    icon={ARROW_LEFT_ICON}
  />
{/if}
{#if completed}
  <div class="completed-badge" class:completed-badge-sm={size === "sm"}>
    <Checkmark16 />
    <span>Completed</span>
  </div>
{:else if showMarkAsCompletedButton}
  {#if effectiveDisabled && disabledTooltip}
    <Tooltip hideIcon direction="bottom" align="end">
      <span slot="triggerText" class="completion-button">
        <Button
          kind="secondary"
          {text}
          size="field"
          disabled
          {iconLeft}
          {icon}
        />
      </span>
      <span class="disabled-tooltip">{disabledTooltip}</span>
    </Tooltip>
  {:else}
    <Button
      kind="secondary"
      {text}
      size="field"
      disabled={effectiveDisabled}
      {iconLeft}
      {icon}
      on:click={handleCompletionClick}
    />
  {/if}
{/if}

{#if confirmBeforeComplete}
  <DangerModal
    danger
    bind:open={confirmationOpen}
    preventCloseOnClickOutside
    modalHeading={text}
    primaryButtonText={text}
    secondaryButtonText="Cancel"
    on:click:button--primary={confirmCompletion}
    on:click:button--secondary={() => (confirmationOpen = false)}
    on:close={() => (confirmationOpen = false)}
  >
    <p>
      {completionWarning}
    </p>
    {#if completionDetails.length}
      <ul class="completion-details">
        {#each completionDetails as detail}
          <li>{detail}</li>
        {/each}
      </ul>
    {/if}
  </DangerModal>
{/if}

<style>
  .completion-details {
    margin-top: var(--control-gap);
    list-style: disc;
    padding-left: 1.25rem;
  }

  .completion-details li {
    display: list-item;
  }

  .completion-button {
    display: inline-flex;
  }

  .disabled-tooltip {
    white-space: pre-line;
  }

  /* Disabled buttons swallow hover in some browsers, the tooltip trigger must receive it. */
  .completion-button :global(button:disabled) {
    pointer-events: none;
  }

  .completed-badge {
    border-radius: 0.5rem;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    white-space: nowrap;
    padding: 0 1rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    box-sizing: border-box;
    height: 3rem;
    background: var(--pax-green);
    color: #1c1c20;
  }

  .completed-badge-sm {
    height: 2.5rem;
  }
</style>
